import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'gatsby';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { Layout } from '@components';
import { usePrefersReducedMotion } from '@hooks';

// Case-study pages share one narrow, readable column (~700px effective inside the
// global `main` padding, which matches the print portfolio's 180mm text width, so
// the rebuilt diagrams translate 1:1).
const StyledMain = styled.main`
  max-width: 1000px;
  counter-reset: none;

  strong {
    color: var(--lightest-slate);
    font-weight: 600;
  }

  p.cs-lead {
    max-width: 780px;
    margin: 0 0 16px;
    color: var(--light-slate);
    font-size: var(--fz-lg);
    line-height: 1.6;
  }

  ul.fancy-list {
    max-width: 780px;
    margin: 12px 0 0;
    li {
      color: var(--light-slate);
      font-size: var(--fz-lg);
      line-height: 1.5;
    }
  }

  code {
    background-color: var(--light-navy);
    color: var(--white);
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
    border-radius: var(--border-radius);
    padding: 0.2em 0.45em;
  }
`;

export const CaseStudy = ({ location, title, children }) => (
  <Layout location={location}>
    <Helmet title={`${title} · Matallah Abdallah`} />
    <StyledMain>
      <div className="breadcrumb">
        <span className="arrow">&larr;</span>
        <Link to="/#projects">Projets</Link>
      </div>
      {children}
    </StyledMain>
  </Layout>
);
CaseStudy.propTypes = {
  location: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

// Progressive scroll-reveal wrapper. Wrap a *whole* logical block (heading +
// paragraphs + diagram) so sibling selectors and grids inside stay intact.
export const Reveal = ({ children, delay = 0, className }) => {
  const ref = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  useEffect(() => {
    if (prefersReducedMotion || !ref.current) {
      return;
    }
    sr.reveal(ref.current, srConfig(delay, 0));
  }, []);
  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
};
Reveal.propTypes = {
  children: PropTypes.node.isRequired,
  delay: PropTypes.number,
  className: PropTypes.string,
};

const StyledHeader = styled.header`
  margin-bottom: 45px;

  .cs-eyebrow {
    margin-bottom: 12px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    font-size: var(--fz-sm);
  }
  .cs-title {
    margin: 0;
    font-size: clamp(44px, 8vw, 72px);
    color: var(--lightest-slate);
  }
  .cs-tagline {
    max-width: 760px;
    margin: 14px 0 0;
    color: var(--lightest-slate);
    font-size: clamp(20px, 3.5vw, 26px);
    font-weight: 500;
    line-height: 1.2;
  }
  .cs-meta {
    margin: 18px 0 0;
    color: var(--slate);
    font-family: var(--font-mono);
    font-size: var(--fz-sm);
  }
`;

export const ProjectHeader = ({ eyebrow, title, tagline, meta = [], children }) => (
  <StyledHeader>
    {eyebrow && <p className="overline cs-eyebrow">{eyebrow}</p>}
    <h1 className="cs-title">{title}</h1>
    {tagline && <p className="cs-tagline">{tagline}</p>}
    {meta.length > 0 && <p className="cs-meta">{meta.join('   ·   ')}</p>}
    {children}
  </StyledHeader>
);
ProjectHeader.propTypes = {
  eyebrow: PropTypes.string,
  title: PropTypes.string.isRequired,
  tagline: PropTypes.string,
  meta: PropTypes.arrayOf(PropTypes.string),
  children: PropTypes.node,
};

const StyledChips = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0;
  margin: 26px 0 0;
  list-style: none;

  li {
    padding: 5px 10px;
    color: var(--light-slate);
    background: var(--light-navy);
    border: 1px solid var(--lightest-navy);
    border-radius: var(--border-radius);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    white-space: nowrap;

    &.hot {
      color: var(--green);
      background: var(--green-tint);
      border-color: rgba(100, 255, 218, 0.3);
    }
  }
`;

export const TechChips = ({ items }) => (
  <StyledChips>
    {items.map((it, i) => (
      <li key={i} className={it.hot ? 'hot' : undefined}>
        {it.label}
      </li>
    ))}
  </StyledChips>
);
TechChips.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string.isRequired, hot: PropTypes.bool }),
  ).isRequired,
};

export const SectionHeading = styled.h2`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 56px 0 18px;
  color: var(--lightest-slate);
  font-size: clamp(22px, 4vw, 26px);
  line-height: 1.2;

  &::before {
    content: '';
    flex-shrink: 0;
    width: 12px;
    height: 12px;
    background: var(--green);
    border-radius: 2px;
  }
`;

export const Note = styled.div`
  max-width: 820px;
  margin: 26px 0;
  padding: 16px 22px;
  color: var(--light-slate);
  background: var(--green-tint);
  border-left: 2px solid var(--green);
  border-radius: 0 var(--border-radius) var(--border-radius) 0;
  font-size: var(--fz-md);
  line-height: 1.55;

  p {
    margin: 0 0 10px;
    &:last-child {
      margin: 0;
    }
  }
  strong,
  b {
    color: var(--green);
    font-weight: 600;
  }
`;

export const Caption = styled.p`
  max-width: 820px;
  margin: 14px 0 0;
  color: var(--slate);
  font-size: var(--fz-sm);
  line-height: 1.5;

  strong,
  b {
    color: var(--lightest-slate);
    font-weight: 600;
  }
  code {
    background-color: var(--light-navy);
    color: var(--white);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    border-radius: var(--border-radius);
    padding: 0.15em 0.4em;
  }
`;

const StyledTechList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  padding: 0;
  margin: 18px 0 0;
  list-style: none;

  li {
    margin: 0 20px 8px 0;
    color: var(--light-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
    white-space: nowrap;

    &::before {
      content: '▹';
      margin-right: 8px;
      color: var(--green);
    }
  }
`;

export const TechList = ({ items }) => (
  <StyledTechList>
    {items.map((t, i) => (
      <li key={i}>{t}</li>
    ))}
  </StyledTechList>
);
TechList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.string).isRequired,
};
